import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import Discussion from 'flarum/common/models/Discussion';
import { ApiResponseSingle } from 'flarum/common/Store';
import Post from 'flarum/common/models/Post';
import PostStream from 'flarum/forum/components/PostStream';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';

import TreePostStreamState from './states/TreePostStreamState';
import { isCreatedAtAsc, isCreatedAtDesc, sortDiscussionPost } from './utils/postSort';
import PostStreamScrubber from 'flarum/forum/components/PostStreamScrubber';

export default function () {
  /**
   * 排序处理
   */
  override(DiscussionPage.prototype, 'show', function (original, discussion: ApiResponseSingle<Discussion>) {
    app.history.push('discussion', discussion.title());
    app.setTitle(discussion.title());
    app.setTitleCount(0);

    if (isCreatedAtDesc()) {
      sortDiscussionPost(discussion, 'desc');
    }

    // When the API responds with a discussion, it will also include a number of
    // posts. Some of these posts are included because they are on the first
    // page of posts we want to display (determined by the `near` parameter) –
    // others may be included because due to other relationships introduced by
    // extensions. We need to distinguish the two so we don't end up displaying
    // the wrong posts. We do so by filtering out the posts that don't have
    // the 'discussion' relationship linked, then sorting and splicing.
    let includedPosts: Post[] = [];
    if (discussion.payload && discussion.payload.included) {
      const discussionId = discussion.id();

      includedPosts = discussion.payload.included
        .filter(
          (record) =>
            record.type === 'posts' &&
            record.relationships &&
            record.relationships.discussion &&
            !Array.isArray(record.relationships.discussion.data) &&
            record.relationships.discussion.data.id === discussionId
        )
        // We can make this assertion because posts should be in the store,
        // since they were in the discussion's payload.
        .map((record) => app.store.getById<Post>('posts', record.id) as Post)
        .sort((a: Post, b: Post) => a.number() - b.number())
        .slice(0, 20);
    }

    // Set up the post stream for this discussion, along with the first page of
    // posts we want to display. Tell the stream to scroll down and highlight
    // the specific post that was routed to.
    this.stream = new TreePostStreamState(discussion, includedPosts);
    const rawNearParam = m.route.param('near');
    const nearParam = rawNearParam === 'reply' ? 'reply' : parseInt(rawNearParam);
    this.stream.goToNumber(nearParam || (includedPosts[0]?.number() ?? 0), true).then(() => {
      this.discussion = discussion;

      app.current.set('discussion', discussion);
      app.current.set('stream', this.stream);
    });
  });

  /**
   * 处理跳转到data-number
   */
  override(PostStream.prototype, 'scrollToItem', function (original, $item, animate, force, reply) {
    const $container = $('html, body').stop(true);
    const number = $item.data('number');
    const index = $item.data('index');

    if ($item.length) {
      const itemTop = $item.offset().top - this.getMarginTop();
      const itemBottom = $item.offset().top + $item.height();
      const scrollTop = $(document).scrollTop();
      const scrollBottom = scrollTop + $(window).height();

      // If the item is already in the viewport, we may not need to scroll.
      // If we're scrolling to the reply placeholder, we'll make sure its
      // bottom will line up with the top of the composer.
      if (force || itemTop < scrollTop || itemBottom > scrollBottom) {
        const top = reply ? itemBottom - $(window).height() + app.composer.computedHeight() : $item.is(':first-child') ? 0 : itemTop;

        if (!animate) {
          $container.scrollTop(top);
        } else if (top !== scrollTop) {
          $container.animate({ scrollTop: top }, 'fast');
        }
      }
    }

    const updateScrubberHeight = () => {
      // We manually set the index because we want to display the index of the
      // exact post we've scrolled to, not just that of the first post within viewport.
      this.updateScrubber();
      if (index !== undefined) this.stream.index = index + 1;
      if (number !== undefined) this.stream.number = number;
    };

    // If we don't update this before the scroll, the scrubber will start
    // at the top, and animate down, which can be confusing
    updateScrubberHeight();
    this.stream.forceUpdateScrubber = true;

    return Promise.all([$container.promise(), this.stream.loadPromise]).then(() => {
      m.redraw.sync();

      // Rendering post contents will probably throw off our position.
      // To counter this, we'll scroll either:
      //   - To the reply placeholder (aligned with composer top)
      //   - To the top of the page if we're on the first post
      //   - To the top of a post (if that post exists)
      // If the post does not currently exist, it's probably
      // outside of the range we loaded in, so we won't adjust anything,
      // as it will soon be rendered by the "load more" system.
      let itemOffset;
      if (reply) {
        const $placeholder = $('.PostStream-item:last-child');
        $(window).scrollTop($placeholder.offset().top + $placeholder.height() - $(window).height() + app.composer.computedHeight());
      } else if (index === 0 || number === 1) {
        $(window).scrollTop(0);
      } else if (index !== undefined && (itemOffset = $(`.PostStream-item[data-index=${index}]`).offset())) {
        $(window).scrollTop(itemOffset.top - this.getMarginTop());
      } else if (number !== undefined && (itemOffset = $(`.PostStream-item[data-number=${number}]`).offset())) {
        $(window).scrollTop(itemOffset.top - this.getMarginTop());
      }

      // We want to adjust this again after posts have been loaded in
      // and position adjusted so that the scrubber's height is accurate.
      updateScrubberHeight();

      this.calculatePosition();
      this.stream.paused = false;
      // Check if we need to load more posts after scrolling.
      this.loadPostsIfNeeded();
    });
  });

  /**
   * 计算滚动位置，判断已浏览过最新的一条消息
   */
  override(PostStream.prototype, 'calculatePosition', function (original, top) {
    const marginTop = this.getMarginTop();
    const $window = $(window);
    const viewportHeight = $window.height() - marginTop;
    const scrollTop = $window.scrollTop() + marginTop;
    const viewportTop = top + marginTop;

    let startNumber = 0;
    let endNumber = 0;

    this.$('.PostStream-item').each(function () {
      const $item = $(this);
      const top = $item.offset().top;
      const height = $item.outerHeight(true);
      const bottom = top + height;
      const visibleTop = Math.max(0, viewportTop - top);

      const threeQuartersVisible = visibleTop / height < 0.75;
      const coversQuarterOfViewport = (height - visibleTop) / viewportHeight > 0.25;
      if (!startNumber && (threeQuartersVisible || coversQuarterOfViewport)) {
        startNumber = $item.data('number');
      }

      if (viewportTop >= top - 30 && viewportTop <= bottom) {
        startNumber = $item.data('number');
      }

      if (top >= viewportTop && bottom <= viewportTop + viewportHeight) {
        if ($item.data('number')) {
          endNumber = Math.max(endNumber || 0, $item.data('number'));
        }
      }

      if (top > viewportTop + viewportHeight) {
        return false;
      }
    });

    if (startNumber) {
      this.attrs.onPositionChange(startNumber, Math.max(startNumber, endNumber));
    }
  });

  /**
   * 移除右侧滚动条未读信息和修改排序文案
   */
  extend(PostStreamScrubber.prototype, 'view', function (vnode) {
    // Dropdown-menu // Scrubber // Scrubber-scrollbar // Scrubber-unread
    delete vnode.children[1].children[0].children[1].children[3].attrs.oncreate;
    delete vnode.children[1].children[0].children[1].children[3].attrs.onupdate;

    if (isCreatedAtDesc()) {
      // Dropdown-menu // Scrubber // scrubber-first // [ // #
      vnode.children[1].children[0].children[0].children[2].children[0].children = app.translator.trans('core.forum.post_scrubber.now_link');

      // Dropdown-menu // Scrubber // scrubber-last // [ // #
      vnode.children[1].children[0].children[2].children[2].children[0].children = app.translator.trans(
        'core.forum.post_scrubber.original_post_link'
      );
    }
  });
}
